import React from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "tecrmi_components/useAuth";
import { LoadingScreen } from "@xplatform/design-system";
import { setGlobalAccessTokenFn } from "@utils/httpClient";
import {
  setAccessToken,
  clearAccessToken,
  setAccessTokenRefresher,
} from "@utils/tokenStore";

interface Props {
  children: ReactNode;
}

/**
 * Bridges the BeeHive `useAuth()` context into:
 *   1. The HttpClient via `setGlobalAccessTokenFn` (for REST calls).
 *   2. The in-memory token store. The token is deliberately NOT persisted: a
 *      bearer token in localStorage is readable by any script on the page and
 *      outlives the tab.
 *
 * Renders a loading state while the initial token is fetched so that the first
 * request never goes out without an Authorization header. Same pattern as the
 * mapping and extraction frontends.
 */
export const ModuleContextProvider: React.FC<Props> = ({ children }) => {
  const { t } = useTranslation();
  const { getValidAccessToken, currentSession, loading } = useAuth();
  const [tokenReady, setTokenReady] = React.useState(false);

  React.useEffect(() => {
    setGlobalAccessTokenFn(getValidAccessToken);
    setAccessTokenRefresher(getValidAccessToken);
  }, [getValidAccessToken]);

  React.useEffect(() => {
    let cancelled = false;
    const storeToken = async (): Promise<void> => {
      const token = await getValidAccessToken();
      if (cancelled) return;
      if (token) {
        setAccessToken(token);
        setTokenReady(true);
      } else {
        clearAccessToken();
        setTokenReady(false);
      }
    };
    if (currentSession) {
      void storeToken();
    } else {
      clearAccessToken();
      setTokenReady(false);
    }
    return () => {
      cancelled = true;
    };
  }, [currentSession, getValidAccessToken]);

  if (loading || (currentSession && !tokenReady)) {
    return <LoadingScreen message={t("common.checkingAuth")} />;
  }

  return <>{children}</>;
};
